/* eslint-disable no-case-declarations */
import { SearchResults } from '../SearchResults/SearchResults.component';
import { TFunction } from 'i18next';
import { SearchContext } from 'pages/recherche';
import { ActivityCode, InterestCode } from 'pleinchamp-api-client';
import React, { FC, useContext, useMemo } from 'react';
import { Text } from '@components/Text/Text.component';
import { ThematicContent } from '@components/ThematicContent/ThematicContent.component';
import { MetasProps, PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { SearchContentType } from '@search/business/search.types';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { fromCamelToKebab } from '@utils/strings';
import classnames from 'classnames';
import { GeneralContext } from 'features/business/app.contexts';
import { ATLevel2 } from 'features/tagPlan/business/TagPlan.types';
import dynamic from 'next/dynamic';
import styles from './SearchLayout.module.scss';

function getMetaProps(interests: InterestCode[], activities: ActivityCode[], t: TFunction): MetasProps {
  const isTag = [...interests, ...activities].length === 1;
  if (!isTag) {
    return {
      metaTitleI18n: 'search.meta-title',
    };
  }

  if (activities.length) {
    const value = fromCamelToKebab(activities[0]);
    const activity = t(`search.filter.activities.${value}`);
    return {
      metaDescI18n: `search.activity.meta-desc.${value}`,
      metaTitleI18n: 'search.activity.meta-title',
      metaTitleOptions: { activity },
    };
  }
  if (interests.length) {
    const theme = t(`search.filter.theme.${fromCamelToKebab(interests[0])}`);
    return {
      metaDescI18n: 'search.theme.meta-desc',
      metaDescOptions: { theme },
      metaTitleI18n: 'search.theme.meta-title',
      metaTitleOptions: { theme },
    };
  }
  throw new Error(`Unknown filter type`);
}

function getTagDetails(
  contentType: SearchContentType,
  query: string | undefined,
  activities: ActivityCode[],
  interests: InterestCode[]
) {
  // We only tag the interests and activities page, not all the other search pages
  const isTag =
    contentType === SearchContentType.All && (!query || query === '') && [...activities, ...interests].length === 1;
  const tagi18ns: string[] = [];
  if (isTag) {
    if (interests.length) {
      tagi18ns.push('search.interests.tag.title', `search.filter.theme.${fromCamelToKebab(interests[0])}`);
    } else if (activities.length) {
      tagi18ns.push('search.activities.tag.title', `search.filter.activities.${fromCamelToKebab(activities[0])}`);
    }
  }
  return tagi18ns;
}

const SearchFilters = dynamic<{}>(
  () => import('../SearchFilters/SearchFilters.component').then((mod) => mod.SearchFilters),
  { ssr: false } // SSR false to fix error Text content does not match server-rendered HTML.
);

const SearchLayout: FC = () => {
  const isMobile = useIsBreakpointDown(Breakpoint.l);
  const { t } = useTranslation(['search']);
  const {
    contentType: { data: tempContentType },
    activities: { data: tempActivities },
    interests: { data: tempInterests },
  } = useContext(SearchContext);
  const { searchQuery } = useContext(GeneralContext);
  const contentType = tempContentType || SearchContentType.All;
  const activities = tempActivities || [];
  const interests = tempInterests || [];

  const { tagProps, tagi18ns } = useMemo(() => {
    const i18ns = getTagDetails(contentType, searchQuery, activities, interests);
    return {
      tagProps: {
        level2: ATLevel2.Search,
        tagPageNameI18nArray: i18ns.length ? i18ns : ['search.tag.title'],
      },
      tagi18ns: i18ns,
    };
  }, [contentType, searchQuery, activities, interests]);

  return (
    <PlcPageLayout tagProps={tagProps} {...getMetaProps(interests, activities, t)}>
      <header className={classnames(styles['search-header'], isMobile ? styles['with-filters-button'] : '')}>
        <>
          {!isMobile && (
            <Text
              className="plc-mb-m"
              i18nKey={tagi18ns.length ? 'search.title.with-tag' : 'search.filter.filter'}
              i18nOptions={{ tag: tagi18ns.length > 1 ? (t(tagi18ns[1]) as string) : '' }}
              tag="h1"
              variant="h3"
            />
          )}
          <SearchFilters />
        </>
      </header>
      <SearchResults />
      <ThematicContent />
    </PlcPageLayout>
  );
};

export { SearchLayout };
